import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import CloseIcon from 'ui/elements/icons/CloseIcon';
import SearchIcon from 'ui/elements/icons/SearchIcon';
import IconButton from 'ui/elements/icons/IconButton';
import TextField from 'ui/elements/form/TextField';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 80px;
`;

interface InlineTextFilterProps {
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  resetSignal?: number;
}

export default function InlineTextFilter({
  onChange,
  placeholder,
  debounceMs = 400,
  resetSignal,
}: InlineTextFilterProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    if (resetSignal !== undefined && inputRef.current) {
      inputRef.current.value = '';
      setHasValue(false);
      clearTimeout(timerRef.current);
    }
  }, [resetSignal]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setHasValue(!!val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onChange(val), debounceMs);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (inputRef.current) inputRef.current.value = '';
    setHasValue(false);
    clearTimeout(timerRef.current);
    onChange('');
  };

  const suffix = (
    <IconButton
      color="grey"
      size="small"
      onClick={hasValue ? handleClear : undefined}
      onMouseDown={stopPropagation}
      aria-label={hasValue ? 'Clear filter' : undefined}
      style={{ opacity: !hasValue && isFocused ? 0 : undefined, pointerEvents: !hasValue ? 'none' : undefined }}
    >
      {hasValue ? <CloseIcon fontSize="small" /> : <SearchIcon fontSize="small" style={{ opacity: 0.4 }} />}
    </IconButton>
  );

  return (
    <Wrapper onClick={stopPropagation} onMouseDown={stopPropagation}>
      <TextField
        inputRef={inputRef}
        placeholder={placeholder ?? 'Filter...'}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        onKeyDown={stopPropagation}
        suffix={suffix}
      />
    </Wrapper>
  );
}
